import { createFileRoute } from "@tanstack/react-router";
import Index from "@/pages/Index";
import heroFirstLarge from "@/assets/hero-kids-boy-laptop-dashboard.webp";
import heroFirstSmall from "@/assets/hero-kids-boy-laptop-dashboard-480.webp";
// landing.css is imported by the lazily-loaded landing chunk, so on a cold load
// it only arrived ~2s in — the hero laid out unstyled first and then jumped
// 341px once it applied (measured 0.34 mobile CLS). Linking the whole 72KB file
// from the head fixed the shift but put a render-blocking request on the
// critical path; instead we inline just the nav/hero/trust-bar slice (generated
// by scripts/build-landing-critical.ts) and let the rest ride the landing chunk.
import landingCriticalCss from "@/components/landing/landing-critical.generated.css?inline";
import interFont from "@/assets/fonts/inter-var-latin.woff2?url";
import jakartaFont from "@/assets/fonts/plus-jakarta-sans-var-latin.woff2?url";
import { SEO, buildHomepageFaqJsonLd } from "@/config/seoConfig";
import { seoHead } from "@/lib/seo/head";
import { getPublicLandingTrialCourse, type PublicTrialCourse } from "@/lib/landingTrial.functions";
import { useQueryClient } from "@tanstack/react-query";
import { publicSubscriptionPlansQuery } from "@/lib/subscriptionPlansQuery";
import { getPublicSubscriptionPlans, type PublicSubscriptionPlan } from "@/lib/subscriptionPlans.functions";
import { resolveTrialPrice } from "@/lib/trialPricing";
import { INTRO_OFFERS_ENABLED } from "@/lib/introPassPath";

export const Route = createFileRoute("/")({
  // The hero/header CTA price must be in the prerendered HTML: read the
  // admin-configured trial course on the server so crawlers and the first paint
  // see the live amount instead of the platform default.
  loader: async (): Promise<{
    trialCourse: PublicTrialCourse | null;
    plans: PublicSubscriptionPlan[] | null;
  }> => {
    // Membership prices ship in the first paint instead of "—" placeholders.
    const [trial, plans] = await Promise.allSettled([
      getPublicLandingTrialCourse(),
      getPublicSubscriptionPlans(),
    ]);
    return {
      trialCourse: trial.status === "fulfilled" ? trial.value : null,
      plans: plans.status === "fulfilled" && plans.value.length ? plans.value : null,
    };
  },
  head: ({ loaderData }) => {
    // Intro offers are paused: no pass price in the homepage FAQ.
    const trialPrice = INTRO_OFFERS_ENABLED ? resolveTrialPrice(loaderData?.trialCourse ?? null) : null;
    const seo = seoHead({
      ...SEO.home,
       jsonLd: [buildHomepageFaqJsonLd(trialPrice ?? resolveTrialPrice(null))],
    });
    return {
      ...seo,
      links: [
        ...seo.links,
        // LCP hero: start the fetch before hydration.

        {
          rel: "preload",
          as: "image",
          href: heroFirstLarge,
          type: "image/webp",
          // React props must be camelCase here; lowercase variants render as
          // invalid DOM properties and leave the preload link mismatched on
          // hydration. Must match the first rotating hero <img> srcset/sizes.
          imageSrcSet: `${heroFirstSmall} 480w, ${heroFirstLarge} 1024w`,
          imageSizes: "(max-width: 980px) min(92vw, 520px), 50vw",
          fetchPriority: "high",
        } as unknown as { rel: string; href: string },
        // The landing critical CSS is now inlined and styles.css loads async, so
        // the browser only discovers these two faces after the async sheet
        // applies — the hero painted in the fallback and reflowed on swap
        // (measured: 0.10-0.32 mobile CLS on the H1 and the CTA). Preloading
        // them costs 75KB on a path that no longer has render-blocking CSS.
        { rel: "preload", as: "font", type: "font/woff2", href: interFont, crossOrigin: "anonymous" },
        { rel: "preload", as: "font", type: "font/woff2", href: jakartaFont, crossOrigin: "anonymous" },
      ],
      styles: [{ children: landingCriticalCss }],
    };
  },
  component: HomeRoute,
});

function HomeRoute() {
  const { trialCourse, plans } = Route.useLoaderData();
  const queryClient = useQueryClient();
  if (plans && !queryClient.getQueryData(publicSubscriptionPlansQuery.queryKey)) {
    queryClient.setQueryData(publicSubscriptionPlansQuery.queryKey, plans);
  }
  return <Index initialTrialCourse={trialCourse} />;
}


